/**
 * Sentry Context Utilities
 *
 * This module provides utilities for Sentry error tracking and logging.
 *
 * Usage examples:
 *
 * // Logging with Sentry's built-in logger
 * import * as Sentry from '@sentry/vue';
 * Sentry.logger.info('User triggered action', { log_source: 'user_action', action_type: 'button_click' });
 * Sentry.logger.warn('Potential issue detected', { log_source: 'validation', field: 'email' });
 * Sentry.logger.error('Operation failed', { log_source: 'api_call', error_code: '500' });
 *
 * // Error tracking with context
 * import { captureErrorWithContext } from '@/utils/sentry-context';
 * captureErrorWithContext(error, { component: 'UserProfile', action: 'save' });
 *
 * // API error tracking (already integrated in fetch.ts)
 * import { captureApiError } from '@/utils/sentry-context';
 *
 * // User action breadcrumbs
 * import { addUserActionBreadcrumb } from '@/utils/sentry-context';
 * addUserActionBreadcrumb('Filter applied', { filter_type: 'date_range' });
 */

import * as Sentry from '@sentry/vue';
import type { Scope } from '@sentry/vue';

/**
 * Get the current user from localStorage
 */
function getCurrentUser(): SecurityUser | null {
  try {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  } catch {
    return null;
  }
}

/**
 * Set user context in Sentry
 * Should be called after successful login/authentication
 */
export function setSentryUserContext(user: SecurityUser | null) {
  if (user) {
    Sentry.setUser({
      id: user.id.toString(),
      email: user.email,
      username: user.name,
      organizationId: user.organizationId.toString(),
      organizationName: user.organizationName,
      roles: user.role?.map(r => r.name).join(', ') || 'none'
    });
  } else {
    Sentry.setUser(null);
  }
}

/**
 * Add common context tags to a Sentry scope
 */
export function addCommonContextToScope(scope: Scope) {
  const user = getCurrentUser();

  // Add user context
  if (user) {
    scope.setUser({
      id: user.id.toString(),
      email: user.email,
      username: user.name
    });
    scope.setTag('user_id', user.id);
    scope.setTag('user_email', user.email);
    scope.setTag('organization_id', user.organizationId);
    scope.setTag('organization_name', user.organizationName);
    scope.setExtra('user_roles', user.role?.map(r => r.name) || []);
  }

  // Add current URL and route context
  scope.setTag('current_url', window.location.href);
  scope.setTag('current_path', window.location.pathname);
  scope.setTag('current_origin', window.location.origin);
  scope.setExtra('url_search_params', window.location.search);
  scope.setExtra('url_hash', window.location.hash);

  // Add browser context
  scope.setTag('user_agent', navigator.userAgent);
  scope.setExtra('viewport_width', window.innerWidth);
  scope.setExtra('viewport_height', window.innerHeight);
}

/**
 * Capture an API error with full context
 */
export function captureApiError(params: {
  errorName: string;
  request: any;
  response?: any;
  error?: any;
  options?: any;
  method?: string | undefined;
  endpoint?: string | undefined;
}) {
  const { errorName, request, response, error, options, method, endpoint } = params;

  Sentry.withScope(scope => {
    // Add common context
    addCommonContextToScope(scope);

    // Add API-specific tags
    scope.setTag('error_type', 'api_error');
    scope.setTag('api_endpoint', endpoint || request);
    scope.setTag('http_method', method || options?.method || 'GET');

    if (response) {
      scope.setTag('http_status', response.status);
      scope.setTag('response_url', response.url);
    }

    // Add detailed error information as extras
    scope.setExtra('request_details', {
      url: request,
      method: method || options?.method || 'GET',
      body: options?.body,
      query: options?.query,
      params: options?.params
    });

    if (response) {
      scope.setExtra('response_details', {
        status: response.status,
        statusText: response.statusText,
        url: response.url,
        headers: response.headers,
        // Only include response body if it's not too large
        body:
          typeof response._data === 'object'
            ? response._data
            : typeof response._data === 'string' && response._data.length < 1000
              ? response._data
              : '[Response body too large or binary]'
      });
    }

    if (error) {
      scope.setExtra('error_details', {
        message: error.message || error,
        stack: error.stack,
        name: error.name
      });
    }

    // Add request headers (excluding sensitive data)
    if (options?.headers) {
      const sanitizedHeaders = { ...options.headers };
      if (sanitizedHeaders.Authorization) {
        sanitizedHeaders.Authorization = '[REDACTED]';
      }
      scope.setExtra('request_headers', sanitizedHeaders);
    }

    // Add timestamp
    scope.setExtra('error_timestamp', new Date().toISOString());

    // Capture the exception
    Sentry.captureException(new Error(errorName));
  });
}

/**
 * Capture a general error with context
 */
export function captureErrorWithContext(error: Error | string, context?: Record<string, any>) {
  Sentry.withScope(scope => {
    addCommonContextToScope(scope);

    if (context) {
      Object.entries(context).forEach(([key, value]) => {
        scope.setExtra(key, value);
      });
    }

    const errorObj = typeof error === 'string' ? new Error(error) : error;
    Sentry.captureException(errorObj);
  });
}

/**
 * Add breadcrumb for navigation or important actions
 */
export function addNavigationBreadcrumb(to: string, from?: string) {
  Sentry.addBreadcrumb({
    category: 'navigation',
    message: `Navigating from ${from || 'unknown'} to ${to}`,
    level: 'info',
    data: {
      to,
      from,
      timestamp: new Date().toISOString()
    }
  });
}

/**
 * Add breadcrumb for user actions
 */
export function addUserActionBreadcrumb(action: string, data?: Record<string, any>) {
  Sentry.addBreadcrumb({
    category: 'user_action',
    message: action,
    level: 'info',
    data: {
      ...data,
      timestamp: new Date().toISOString()
    }
  });
}
